import { toast } from "@/components/Toast";
import events from "@/utils/events";
import { Editor_Words } from "@/utils/lang/editor";
import { SafeOp } from "@/utils/safeOp";
import { langStore, translate, useIsReady } from "@noli/business";
import { Logger } from "@noli/core";
import { cloneDeep } from "lodash";
import { loginStore } from "../login/store";
import { cloudProjectsManager } from "./cloudManager";
import { AutoSaveEvent } from "./hooks/useSaveProjectEvery";
import { localCacheManager } from "./localManager";
import {
  getProjectLatest,
  postDeleteCloudProject,
  postSaveCloudProject,
  postSwitchCloudProject,
} from "./request";
import { CloudProjectType, CurrentCloudProject, PostCloudProjectResponse } from "./types/cloud";
import { LocalCacheType } from "./types/local";
import { DEFAULT_PROJECT_CONFIG, ProjectTransformer, snapshotBoard } from "./utils";

const checkIsConflictError = (err?: any) =>
  err.code === 8000 || err.msg === "ERR_CLOUD_STORAGE_DATA_EXPIRED";
export class ProjectContentProcessor {
  /**
   * 将 云端数据 同步到本地
   * @param project
   * @param failed
   * @returns
   */
  static async setLocalCacheFromCloudProject(
    project: CloudProjectType,
    failed: (e: any, project: CloudProjectType) => Promise<LocalCacheType | undefined>,
  ): Promise<LocalCacheType | undefined> {
    try {
      const localized = await ProjectTransformer.cloudToLocal(project);
      localCacheManager.setData(localized);
      return localized;
    } catch (e) {
      return failed(e, project);
    }
  }

  /**
   * 把本地数据设置到 cloudProjectStore
   * @param localCache
   */
  static setCloudProjectFromLocalCache(localCache: LocalCacheType) {
    const currentData: CurrentCloudProject = ProjectTransformer.localToCurrent(localCache);
    cloudProjectsManager.setCurrentProject(currentData);
  }

  /**
   * 保存本地数据 并 同步本地数据存到云端
   */
  static saveLocalAndCloudProject = () => {
    if (localCacheManager.isSameUser()) {
      return Promise.reject("User has changed!");
    }
    localCacheManager.setDataFromBoard(loginStore.userInfo.userId);
    return ProjectContentProcessor.syncLocalCacheToCloud();
  };

  /**
   * save core（所有项目保存都是调用这个函数）
   *
   * 将本地缓存格式的数据存到云端
   * 1. 没有直接读取本地缓存，而是从参数传入
   * 2. 生成预览图
   * 3. 更新 commit_id
   * 4. 处理了项目冲突情况
   * 5. 更新云存储时间、保存中状态
   * */
  static async postLocalToCloud(
    localCache: LocalCacheType,
    opt?: {
      isCreate?: boolean;
      isOverwrite?: boolean;
    },
  ): Promise<PostCloudProjectResponse> {
    Logger.debug(`>> post to cloud`);
    const boardReady = useIsReady();
    if (!boardReady) throw new Error("should sync to cloud after board ready");
    const cloudCacheJson = JSON.stringify(ProjectTransformer.localToCloud(localCache));
    const preview = snapshotBoard(loginStore.userInfo.userId, opt?.isCreate);
    const postCloudProjectData = {
      id: localCache.id,
      name: localCache.name,
      cloudCacheJson,
      commitId: opt?.isOverwrite ? undefined : localCache?.commit_id,
      preview,
      isOverwrite: opt?.isOverwrite,
    };

    cloudProjectsManager.setSaving(true);
    return postSaveCloudProject(postCloudProjectData)
      .then(async (res) => {
        // should update commit before fetch list
        if (res && res.data?.commit_id) {
          localCacheManager.setCommitId(res.data.commit_id);
        }
        cloudProjectsManager.setLastSaveTime(new Date());
        return res;
      })
      .catch((err) => {
        console.log("err: ", err);
        if (checkIsConflictError(err)) {
          cloudProjectsManager.setConflictModalOpen(true);
          events.emit(AutoSaveEvent.Stop);
        }
        return Promise.reject(err);
      })
      .finally(() => {
        cloudProjectsManager.setSaving(false);
      });
  }

  /**
   * 同步本地缓存到云端
   */
  static async syncLocalCacheToCloud(): Promise<PostCloudProjectResponse | false> {
    const localCache = localCacheManager.data;
    console.log("syncLocalCacheToCloud localCache: ", localCache);

    if (!localCache) return false;
    return ProjectContentProcessor.postLocalToCloud(localCache);
  }
}

/**
 * @name loadCache
 * @description 初始化项目数据，涉及多种情况。
 * PS：不做保存项目的操作，在画板初始化后再做！
 *
 * 几种缓存情况：
 * 1. 无本地缓存
 * 2. 有本地缓存，但无 userId
 * 3. 有本地缓存，有 userId，但 userId 不一致
 * 4. 有本地缓存，有 userId，且与当前用户 userId 一致
 * 5. 有云端项目
 * @returns
 */
export async function loadCache() {
  console.log("loadCache");
  const userId = loginStore.userInfo.userId;
  const localCache = localCacheManager.data;
  console.log("localCache: ", cloneDeep(localCache));
  console.log("localCache?.userId: ", localCache?.userId);

  // 游客
  if (loginStore.isGuest || !userId) {
    if (!localCache || !!localCache.userId) {
      // 没有本地缓存，新建一个本地项目
      console.log("未登录 & 没有本地项目，或本地项目是属于登录用户的，新建一个本地项目");
      const defaultLocalCache = DEFAULT_PROJECT_CONFIG.localCache();
      defaultLocalCache.userId = undefined;
      defaultLocalCache.commit_id = undefined;
      localCacheManager.setData(defaultLocalCache);
      return { cache: defaultLocalCache, needSave: false };
    } else {
      return {
        cache: localCache,
        needSave: false,
      };
    }
  }

  console.log("已登录");

  const { list } = await cloudProjectsManager.getCloudProjects();
  let latestProject: CloudProjectType | null = list[0] ?? null;

  // 无本地缓存或本地缓存的 userId 与当前用户的 userId 不一致
  if (!localCache || !localCache.userId || localCache.userId !== userId) {
    !localCache && console.log("无本地缓存");
    localCache.userId !== userId && console.log("本地缓存的 userId 与当前用户的 userId 不一致");
    // 如果有云端最新项目
    if (latestProject) {
      console.log("如果有云端最新项目");
      const localCache = await ProjectTransformer.cloudToLocal(latestProject);
      localCacheManager.setData(localCache);
      cloudProjectsManager.setCurrentProject(latestProject);
      return { cache: localCache, needSave: false };
    }

    // 没有云端项目，新建一个本地项目 (needSave)
    console.log("没有云端项目，新建一个本地项目");
    const defaultLocalCache = DEFAULT_PROJECT_CONFIG.localCache();
    localCacheManager.setData(defaultLocalCache);
    ProjectContentProcessor.setCloudProjectFromLocalCache(defaultLocalCache);
    return { cache: defaultLocalCache, needSave: true };
  }

  // 有本地缓存并且是属于当前用户的
  // 1. 检查是否存在这个云端项目
  // - 如果存在 && 云端项目比本地更新时间新
  //    - 使用云端
  // - else (如果不存在 || 本地项目比云端新)
  //    - 返回本地缓存 (needSave)
  console.log("有本地缓存并且是属于当前用户的");

  // 检查本地项目和云端的关系
  if (latestProject?.project_id !== localCache.id) {
    latestProject = await getProjectLatest(localCache.id)
      .then((res) => {
        return res.data?.project ?? null;
      })
      .catch((err) => {
        // 云端没有本地这个项目
        console.error(">>> get Latest Version failed", err);
        return null;
      });
  }

  if (latestProject && latestProject.update_time >= Math.ceil(localCache.updateTime / 1000)) {
    console.log("有云端最新项目 且 云端更新时间比本地更新时间新");
    const localCache = await ProjectTransformer.cloudToLocal(latestProject);
    localCacheManager.setData(localCache);
    cloudProjectsManager.setCurrentProject(latestProject);
    return { cache: localCache, needSave: false };
  } else {
    console.log("没有云端最新项目 或 云端更新时间比本地更新时间旧");
    ProjectContentProcessor.setCloudProjectFromLocalCache(localCache);
    return {
      cache: localCache,
      needSave: true,
    };
  }
}

/**
 * @name saveProjectEvery
 * @description 每隔 60s 保存一次本地项目到云端
 * @param second
 * @param updateTimer
 * @returns
 */
export function saveProjectEvery(second: number, updateTimer: (timerId: NodeJS.Timeout) => void) {
  Logger.debug("period saving");
  const timerId = setTimeout(() => {
    ProjectContentProcessor.saveLocalAndCloudProject()
      .then(() => {
        saveProjectEvery(second, updateTimer);
      })
      .catch((err) => {
        if (checkIsConflictError(err)) {
          return;
        }
        saveProjectEvery(second, updateTimer);
        console.error("saveLocalAndCloudProject error happened in saveProjectEvery: ", err);
      });
  }, second * 1000);
  updateTimer(timerId);

  return timerId;
}

class CloudProjectActions {
  /** 保存某项目 */
  saveCloudProjectFrom = async (
    localCache: LocalCacheType,
    refreshList = true,
    isCreate = false,
  ): Promise<PostCloudProjectResponse> => {
    Logger.debug(`>> post to cloud`);
    const boardReady = useIsReady();
    if (!boardReady) throw new Error("should sync to cloud after board ready");

    return ProjectContentProcessor.postLocalToCloud(localCache, { isCreate })
      .then(async (res) => {
        if (refreshList) {
          await cloudProjectsManager.refreshCloudProjects();
        }
        return res;
      })
      .catch((err) => {
        console.log("err: ", err);
        return Promise.reject(err);
      });
  };

  /**
   * 保存当前项目
   * @returns
   */
  saveCurrentProject = async () => {
    return ProjectContentProcessor.saveLocalAndCloudProject();
  };

  /** 项目重命名 */
  updateProjectName = (id: string, newName: string) => {
    const localCache = localCacheManager.data ? { ...localCacheManager.data } : null;
    if (!localCache) return Promise.reject("no localCache");

    localCache.name = newName;
    return this.saveCloudProjectFrom(localCache).then((res) => {
      if (res && res?.code === 0) {
        /**
         * 更新本地缓存的项目名
         */
        localCacheManager.setName(newName);
        /** 更新 store 数据 */
        if (!cloudProjectsManager.currentProject) {
          cloudProjectsManager.setCurrentProject({
            project_id: id,
            project_name: newName,
            update_time: new Date().getTime(),
            commit_id: res.data?.commit_id,
          });
        } else {
          cloudProjectsManager.updateCurrentProject("project_name", newName);
        }
      } else {
        return Promise.reject(res ? res.msg : "update project name failed");
      }
    });
  };

  /** 纯切换项目 */
  private replaceCloudProject = async (project: CloudProjectType) => {
    // 更新云端当前项目
    const switchRes = await postSwitchCloudProject(project.project_id).catch((e) => {
      console.error(" 项目切换 e: ", e);
      toast({
        status: "error",
        title: translate(Editor_Words["cloud_project_switch_failed"], langStore.tgt),
      });
    });

    // 云端切换成功
    if (switchRes && switchRes.code === 0 && switchRes.data) {
      project.commit_id = switchRes.data.commit_id;
      localCacheManager.setCommitId(switchRes.data.commit_id);

      // 更新云端项目列表
      cloudProjectsManager.refreshCloudProjects();

      const failed = async (e: any) => {
        Logger.error(
          `internal error occorred when 'replaceCloudProject -> syncCloudProjectToLocalCache' : ${e}`,
        );
        throw Error("Board replace failed" + e.toString());
      };

      const success = async () => {
        toast({
          title: `${translate(Editor_Words["cloud_project_switch_success"], langStore.tgt)}${
            project.project_name
          }`,
          status: "success",
        });
        // 更新本地
        await ProjectContentProcessor.setLocalCacheFromCloudProject(project, failed);
        // 更新 currentProject
        cloudProjectsManager.setCurrentProject(project);
      };

      await SafeOp.replaceCloudProject(project, {
        success,
        failed,
      });
    }
  };

  /**
   * 切换项目
   * @param project
   */
  saveAndReplaceProject = async (project: CloudProjectType) => {
    // 暂停自动保存
    events.emit(AutoSaveEvent.Stop);
    // 保存当前项目
    const saveRes = await ProjectContentProcessor.saveLocalAndCloudProject()
      .then((res) => {
        if (res && res?.code === 0) {
          return res;
        }
        return Promise.reject("本地项目保存失败");
      })
      .catch((err) => {
        console.error(">>> saveLocalAndCloudProject err: ", err);
        if (checkIsConflictError(err)) {
          return;
        }
        // 重启自动保存
        events.emit(AutoSaveEvent.Start);
      });

    if (!saveRes) {
      return;
    }
    // 切换画板项目
    await this.replaceCloudProject(project);
    // 重启自动保存
    events.emit(AutoSaveEvent.Start);
  };

  /**
   * 新建项目
   * @param projectName
   */
  createCloudProject = async (projectName: string) => {
    // 暂停自动保存
    events.emit(AutoSaveEvent.Stop);
    // 创建新的默认项目数据
    const newLocalCache = DEFAULT_PROJECT_CONFIG.localCache();
    newLocalCache.name = projectName;

    const saveRes = await ProjectContentProcessor.saveLocalAndCloudProject()
      .then((res) => {
        if (res && res?.code === 0) {
          return res;
        }
        return Promise.reject("本地项目保存失败");
      })
      .catch((err) => {
        console.error(">>> saveLocalAndCloudProject err: ", err);
        // 重启自动保存
        events.emit(AutoSaveEvent.Start);
      });

    if (!saveRes) return;

    // const oldCommit = cloudStorage.get(COMMIT_ID_CACHE_KEY);
    // 创建新项目
    const newProject = await this.saveCloudProjectFrom(newLocalCache, true, true)
      .then((res) => {
        if (res && res.code === 0) {
          const newProject = cloudProjectsManager.list.find(
            (project) => project.project_id === newLocalCache.id,
          );
          toast({
            title: translate(Editor_Words["cloud_project_create_success"], langStore.tgt),
            status: "success",
          });

          if (!newProject) return Promise.reject(res);
          return newProject;
        } else {
          return Promise.reject(res);
        }
      })
      .catch((e) => {
        console.error("create new project error: ", e);
        toast({
          status: "error",
          title: translate(Editor_Words["cloud_project_create_failed"], langStore.tgt),
        });
      });

    // 切换项目
    if (newProject) {
      await this.replaceCloudProject(newProject);
    }

    // 重启自动保存
    events.emit(AutoSaveEvent.Start);
  };

  /**
   * 删除云端项目
   * @param projectId
   * @returns
   */
  deleteCloudProject(projectId: string) {
    return postDeleteCloudProject(projectId)
      .then((res: any) => {
        if (res && res.code === 0) {
          cloudProjectsManager.refreshCloudProjects();
          // cloudProjectsManager.deleteCloudProject(projectId);
        }
      })
      .catch(() => {
        // TODO:文案
        toast({ status: "error", title: "项目未能删除" });
      });
  }

  /**
   * 云端覆盖本地
   */
  async useCloudToReplaceLocal(project: CloudProjectType) {
    /**
     * 1. 设置当前云端项目
     * 2. 设置本地数据
     * 3. 替换画板数据
     */
    cloudProjectsManager.setCurrentProject(project);
    const localized = await ProjectContentProcessor.setLocalCacheFromCloudProject(project, failed);
    if (!localized) {
      return;
    }
    return SafeOp.replaceCloudProject(project)
      .then(() => {
        return true;
      })
      .catch((err) => {
        failed(err);
      });

    function failed(err: any) {
      return Promise.reject(err);
    }
  }

  /**
   * 本地覆盖云端
   */
  async useLocalToReplaceCloud(localCache: LocalCacheType) {
    /**
     * 1. 保存本地项目到云端（使用覆盖）
     * 2. 更新本地（commit_id) (在 postLocalToCloud 里)
     * 3. 更新cloudProject
     */
    return ProjectContentProcessor.postLocalToCloud(localCache, { isOverwrite: true }).then(() => {
      console.log("localCache: ", localCache);
      const currentLocalCache = localCacheManager.data;
      ProjectContentProcessor.setCloudProjectFromLocalCache(currentLocalCache);
    });
  }

  /**
   * 保留云端和本地
   * （给本地项目新建一个云端项目）
   */
  async saveLocalAsNew(localCache: LocalCacheType) {
    console.log("saveLocalAsNew: ");
    /**
     * 1. 新建本地项目到云端
     * 2. 更新本地（commit_id) (在 postLocalToCloud 里)
     * 3. 更新 localCache 的 id 和 name
     * 4. 更新 cloudProject
     */
    const cloudJson = ProjectTransformer.localToCloud(localCache);
    const { name } = localCache;
    const newProject = {
      id: DEFAULT_PROJECT_CONFIG.projectId(),
      name: `${name}的副本`,
      cloudCacheJson: JSON.stringify(cloudJson),
    };
    const newLocalCache = cloneDeep(localCache);
    newLocalCache.id = newProject.id;
    newLocalCache.name = newProject.name;
    newLocalCache.commit_id = undefined;

    return ProjectContentProcessor.postLocalToCloud(newLocalCache).then(async (res) => {
      console.log("newLocalCache: ", newLocalCache);
      if (res.code === 0) {
        // 云端项目数据
        const currentProject: CurrentCloudProject = {
          project_id: newProject.id,
          project_name: newProject.name,
          commit_id: res.data?.commit_id,
          update_time: localCache.updateTime,
        };
        console.log("currentProject: ", currentProject);

        // 本地缓存更新
        localCacheManager.setData({
          id: newProject.id,
          name: newProject.name,
          updateTime: currentProject.update_time,
        });

        // 云端项目更新
        cloudProjectsManager.setCurrentProject(currentProject);
      }
      return res;
    });
  }
}

export const cloudProjectActions = new CloudProjectActions();
